import { useState, createContext, type ReactNode, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { deleteFromSessionStorage, getFromSessionStorage, setInSessionStorage } from '@/util/storage'
import { ONBOARDING_STATUS_KEY, ONBOARDING_STEP_KEY } from './OnboardingContext'
export interface Session {
  accessToken?: string
  eventId: string
  participationId: string
  parentName: string
  parentSlug: string
  schoolName: string
  schoolSlug: string
  isParticipationMutable: boolean
}

interface SessionContext {
  session?: Session
  startSession: (session: Session) => void
  endSession: () => void
}

export const SESSION_STORAGE_KEY = 'parent-session'

export const ACCESS_TOKEN_STORAGE_KEY = 'access-token'
export const REFRESH_TOKEN_STORAGE_KEY = 'refresh-token'
const SessionContext = createContext<SessionContext | null>(null)

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const [sessionData, setSessionData] = useState<Session | undefined>(() => {
    const sessionData = getFromSessionStorage<Session>(SESSION_STORAGE_KEY)
    Sentry.logger.debug(Sentry.logger.fmt`Initializing Session Data: ${sessionData ? 'Active Session' : 'No Session'}`)
    return sessionData
  })

  useEffect(() => {
    if (sessionData) {
      Sentry.logger.debug(Sentry.logger.fmt`Updating Session Data (${sessionData.participationId})`)
      setInSessionStorage(SESSION_STORAGE_KEY, sessionData)
    }
  }, [sessionData])

  const startSession = (session: Session) => {
    if (session) {
      Sentry.logger.info(Sentry.logger.fmt`New Session Started  (${session.participationId})`)
      setSessionData(session)
    }
  }

  const endSession = () => {
    Sentry.logger.info(Sentry.logger.fmt`Session Ending  (${sessionData?.participationId})`)
    setSessionData(undefined)
    deleteFromSessionStorage(SESSION_STORAGE_KEY)
    deleteFromSessionStorage(ACCESS_TOKEN_STORAGE_KEY)
    deleteFromSessionStorage(REFRESH_TOKEN_STORAGE_KEY)
    deleteFromSessionStorage(ONBOARDING_STATUS_KEY)
    deleteFromSessionStorage(ONBOARDING_STEP_KEY)
  }

  return (
    <SessionContext.Provider
      value={{
        session: sessionData,

        startSession,
        endSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}

export default SessionContext
